import axios, {
  AxiosError,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
  isAxiosError,
} from 'axios';
import { clearSessionState, hasStoredSession } from '@/services/session/clear-session-state';
import { signInUrlPreserving } from '@/services/session/sign-in-redirect';
import { resetTerminal } from '@/services/websocket/ws-client';
import { DownloadedFile, ResponseTransformer } from './request.types';

export const BASE_API_URL = import.meta.env.VITE_BASE_API_URL;

export const httpClient = axios.create({
  baseURL: BASE_API_URL,
  withCredentials: true,
  timeout: 30000,
});

const UNSAFE_METHODS = ['POST', 'PUT', 'PATCH', 'DELETE'];

let csrfToken: string | null = null;
let pendingCsrfToken: Promise<string | null> | null = null;

function loadCsrfToken(): Promise<string | null> {
  pendingCsrfToken ??= httpClient
    .get<{ csrf_token: string }>('/auth/csrf')
    .then(({ data }) => (csrfToken = data.csrf_token))
    .catch(() => null)
    .finally(() => {
      pendingCsrfToken = null;
    });
  return pendingCsrfToken;
}

async function withCsrfHeader(config: AxiosRequestConfig): Promise<AxiosRequestConfig> {
  if (!UNSAFE_METHODS.includes((config.method ?? 'GET').toUpperCase())) return config;

  const token = csrfToken ?? (await loadCsrfToken());
  if (!token) return config;

  return { ...config, headers: { ...config.headers, 'X-CSRFToken': token } };
}

// A 401 from any of these is a rejected credential, not an expired session. /auth/refresh is
// listed so a failing refresh cannot recurse into itself. /auth/password and /auth/sign_out
// are absent on purpose: both are worth renewing for, and endSession's hasStoredSession guard
// is what keeps an anonymous 401 from redirecting.
const SESSION_EXEMPT_PATHS = [
  '/auth/refresh',
  '/auth/csrf',
  '/auth/sign_in',
  '/auth/send_code',
  '/auth/verify_code',
  '/auth/register',
  '/auth/verify_email',
  '/auth/resend_verification',
];

type RetriableConfig = InternalAxiosRequestConfig & { _sessionRetried?: boolean };

let pendingRefresh: Promise<RefreshOutcome> | null = null;
let sessionEnded = false;

function isSessionExempt(url: string | undefined): boolean {
  if (!url) return false;
  const path = url.split('?')[0].replace(/\/+$/, '');
  return SESSION_EXEMPT_PATHS.includes(path);
}

// 'rejected' is the only outcome that means the session is gone. 'blocked' covers throttling,
// server errors and being offline — none of which say anything about the credential, and all
// of which would otherwise log out everyone sharing an address.
export type RefreshOutcome = 'ok' | 'rejected' | 'blocked';

function outcomeOf(error: unknown): RefreshOutcome {
  return (error as AxiosError).response?.status === 401 ? 'rejected' : 'blocked';
}

async function postRefresh(): Promise<void> {
  // Through withCsrfHeader because this route needs the CSRF header like any other write.
  await httpClient.request(await withCsrfHeader({ method: 'POST', url: '/auth/refresh' }));
}

async function attemptRefresh(): Promise<RefreshOutcome> {
  try {
    await postRefresh();
    return 'ok';
  } catch (error) {
    // 403 means the CSRF token this tab cached is no longer accepted. Refetch and dial once
    // more, or a stale token reads as a dead session for the rest of the page's life.
    if ((error as AxiosError).response?.status !== 403) return outcomeOf(error);
    csrfToken = null;
    try {
      await postRefresh();
      return 'ok';
    } catch (retryError) {
      return outcomeOf(retryError);
    }
  }
}

function refreshSession(): Promise<RefreshOutcome> {
  // Same single-flight shape as loadCsrfToken: concurrent 401s share one call, and the slot
  // clears afterwards so a later expiry can dial again.
  pendingRefresh ??= attemptRefresh()
    .then((outcome) => {
      // The sockets expired with the access token and the server closed them 4401. Nothing
      // else re-dials, so a renewed session has to, or realtime stays dead until a reload.
      if (outcome === 'ok') resetTerminal();
      return outcome;
    })
    .finally(() => {
      pendingRefresh = null;
    });
  return pendingRefresh;
}

/**
 * Renew the session for callers outside the HTTP path.
 *
 * The WebSocket client needs this. A 4401 close and an HTTP 401 both come off the access
 * token's exp, so they arrive together, but an idle tab issues no request for the interceptor
 * to hook. Shares the single-flight slot above, so the two paths dial once between them.
 *
 * Returns the outcome rather than a boolean: 'blocked' collapsed into false would have this
 * caller end a session over a 429 or a network blip, which is the opposite of what the
 * interceptor below does with the same value.
 */
export async function renewSession(): Promise<RefreshOutcome> {
  return refreshSession();
}

// The header captured on the original request predates the refresh, and attemptRefresh's 403
// branch throws the cached token away and fetches a new one. Replaying the config as-is would
// send the token this tab already decided was stale.
async function replay(config: RetriableConfig) {
  return httpClient.request(await withCsrfHeader(config));
}

async function endSession(): Promise<void> {
  if (sessionEnded) return;
  // Nothing to end for an anonymous visitor. The password-reset page 401s without a session,
  // and redirecting would throw away a form the visitor is part-way through. Trade-off: a
  // browser whose flag was cleared by hand keeps whatever cookie is in the jar, since this
  // returns before the revocation call.
  if (!hasStoredSession()) return;
  sessionEnded = true;
  // Ask the server to drop the refresh cookie. It is HttpOnly, so nothing here can clear it,
  // and leaving it live would keep a usable credential in the jar after the UI has logged out.
  // Awaited, not fire-and-forget: assigning location.href tears the document down, which can
  // cancel a request still in flight. Failure is ignored — the session may already be gone.
  await deleteRequest('/auth/sign_out').catch(() => undefined);
  clearSessionState();
  window.location.href = signInUrlPreserving(window.location);
}

httpClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as RetriableConfig | undefined;
    if (
      error.response?.status !== 401 ||
      !config ||
      config._sessionRetried ||
      sessionEnded ||
      isSessionExempt(config.url)
    ) {
      return Promise.reject(error);
    }

    config._sessionRetried = true;
    const outcome = await refreshSession();

    // Throttled, offline, or the server is unwell. None of that says the credential is bad,
    // and treating it as death logs out everyone sharing one address.
    if (outcome === 'blocked') return Promise.reject(error);

    // Replay even after a rejected refresh: another tab may have renewed the session while
    // this one was asking, in which case the jar already holds a good credential.
    try {
      return await replay(config);
    } catch (replayError) {
      if ((replayError as AxiosError).response?.status !== 401) {
        return Promise.reject(replayError);
      }
      // Still 401 after a refresh this tab did not win. The single-flight slot has cleared,
      // so one more dial picks up whatever the winner left behind before giving up.
      if (outcome === 'rejected' && (await refreshSession()) === 'ok') {
        try {
          return await replay(config);
        } catch (secondError) {
          if ((secondError as AxiosError).response?.status === 401) await endSession();
          return Promise.reject(secondError);
        }
      }
      await endSession();
      return Promise.reject(replayError);
    }
  }
);

async function request<T>(
  config: AxiosRequestConfig,
  responseTransformer?: ResponseTransformer<T>
) {
  const response = await httpClient.request<T>(await withCsrfHeader(config));
  return responseTransformer ? responseTransformer(response) : response.data;
}

export function getRequest<T>(
  url: string,
  config?: AxiosRequestConfig,
  responseTransformer?: ResponseTransformer<T>
) {
  return request<T>({ method: 'GET', url, ...config }, responseTransformer);
}

export function putRequest<T>(
  url: string,
  config?: AxiosRequestConfig,
  responseTransformer?: ResponseTransformer<T>
) {
  return request<T>({ method: 'PUT', url, ...config }, responseTransformer);
}

export function postRequest<T>(
  url: string,
  config?: AxiosRequestConfig,
  responseTransformer?: ResponseTransformer<T>
) {
  return request<T>({ method: 'POST', url, ...config }, responseTransformer);
}

export function patchRequest<T>(
  url: string,
  config?: AxiosRequestConfig,
  responseTransformer?: ResponseTransformer<T>
) {
  return request<T>({ method: 'PATCH', url, ...config }, responseTransformer);
}

export function deleteRequest<T>(
  url: string,
  config?: AxiosRequestConfig,
  responseTransformer?: ResponseTransformer<T>
) {
  return request<T>({ method: 'DELETE', url, ...config }, responseTransformer);
}

const ATTACHMENT_NAME = /filename="?([^";]+)"?/i;

/**
 * A file download, read as a Blob rather than text: decoding it to a string drops the UTF-8
 * byte order mark that makes Excel read Polish letters correctly. The name comes from
 * Content-Disposition, which the API exposes through CORS.
 */
export async function getFileRequest(
  url: string,
  fallbackName: string,
  config?: AxiosRequestConfig
): Promise<DownloadedFile> {
  try {
    return await getRequest(url, { ...config, responseType: 'blob' }, (response) => ({
      blob: response.data as Blob,
      fileName:
        ATTACHMENT_NAME.exec(String(response.headers['content-disposition'] ?? ''))?.[1] ??
        fallbackName,
    }));
  } catch (error) {
    // The error body arrives as a Blob too. As text, getApiErrorMessage can still read it.
    if (isAxiosError(error) && error.response?.data instanceof Blob) {
      error.response.data = await error.response.data.text();
    }
    throw error;
  }
}
